import { useNavigate, useLocation, NavLink, Outlet, Link } from 'react-router-dom'
import { Home, Upload, LogOut, User, Settings, DollarSign, Edit3, Package, Globe2, MessageSquare, Search, ChevronLeft, ChevronRight, Layers, Map, QrCode, Sparkles, Bell, Smartphone, Image } from 'lucide-react'
import { useState } from 'react'
import { useAuthStore } from '../services/authStore'
import ThemeToggle from './theme/ThemeToggle'
import brandMark from '../assets/branding/savinfy-receipts-mark.png'
import NotificationBell from './notifications/NotificationBell'

const SIDEBAR_STORAGE_KEY = 'receipt-admin-sidebar-open'

export default function Layout() {
  const navigate = useNavigate()
  const location = useLocation()
  const [isOpen, setIsOpen] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true'
  })
  const { user, logout } = useAuthStore()

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const routeLabels = {
    '/': 'Dashboard',
    '/receipts': 'Receipts',
    '/categories': 'Product Categories',
    '/admin/categories/hybrid': 'Hybrid Categorization',
    '/admin/categories/hybrid/cache': 'Hybrid Categorization Cache',
    '/admin/categories/exclusion-phrases': 'QR Exclusion Phrases',
    '/upload': 'Upload',
    '/scan': 'Scan QR',
    '/receipt-products': 'Products',
    '/queue/receipt-analysis': 'Receipt Queue',
    '/queue/product-analysis': 'Product Queue',
    '/queue/product-translation': 'Product Translation',
    '/queue/qrcode-product-extraction': 'QR Code Extraction',
    '/admin/users': 'Users',
    '/admin/llm-settings': 'Queue settings',
    '/admin/llm-costs': 'LLM Cost Accounting',
    '/admin/receipt-image-limits': 'Receipt image limits',
    '/admin/exchange-rates': 'Exchange Rates',
    '/admin/languages': 'Languages',
    '/admin/translations': 'Translations',
    '/profile': 'Profile',
    '/legal/documents': 'Legal Documents',
    '/admin/legal': 'Legal Management',
    '/admin/ocr-settings': 'OCR & LLM settings',
    '/admin/queue-workers': 'Workers Stats',
    '/admin/selectors': 'Selectors',
    '/admin/metrics': 'Metrics',
    '/admin/llm-cache': 'LLM Image Queue Cache',
    '/admin/receipt-image-assets': 'Receipt Image Assets',
    '/admin/jobs': 'Job Detail',
    '/admin/public-recognitions': 'Public API Recognitions',
    '/admin/currency-transition-rules': 'Currency Rules',
    '/admin/currency-normalization': 'Currency Normalization',
    '/admin/public-api/limits': 'Public API Limit Policies',
    '/admin/public-api/usage': 'Public API Usage',
    '/admin/mobile-devices': 'Mobile Devices',
    '/admin/telegram-mini-app-users': 'Telegram Mini App Users',
    '/admin/mobile/releases': 'APK Releases',
  }

  const menuGroups = [
    {
      title: 'General',
      items: [
        { to: '/', label: 'Dashboard', icon: <Home size={18} /> },
        { to: '/receipts', label: 'Receipts', icon: <QrCode size={18} /> },
        { to: '/receipt-products', label: 'Products', icon: <Package size={18} /> },
        { to: '/legal/documents', label: 'Legal Documents', icon: <Layers size={18} /> },
      ],
    },
    {
      title: 'Catalog',
      items: [
        { to: '/categories', label: 'Product Categories', icon: <Edit3 size={18} /> },
        { to: '/admin/categories/hybrid', label: 'Hybrid Categorization', icon: <Sparkles size={18} /> },
      ],
    },
    {
      title: 'Workflow',
      items: [
        { to: '/upload', label: 'Upload', icon: <Upload size={18} /> },
        { to: '/scan', label: 'Scan QR', icon: <QrCode size={18} /> },
      ],
    },
    {
      title: 'Queue',
      items: [
        { to: '/queue/receipt-analysis', label: 'Receipt Queue', icon: <Package size={18} /> },
        { to: '/queue/product-analysis', label: 'Product Queue', icon: <Layers size={18} /> },
        { to: '/queue/product-translation', label: 'Translation Queue', icon: <Map size={18} /> },
        { to: '/queue/qrcode-product-extraction', label: 'QR Code Queue', icon: <QrCode size={18} /> },
      ],
    },
  ]

  const adminMenu = [
    { to: '/admin/users', label: 'Users', icon: <User size={18} /> },
    { to: '/admin/ocr-settings', label: 'OCR & LLM settings', icon: <Settings size={18} /> },
    { to: '/admin/receipt-image-limits', label: 'Receipt image limits', icon: <Image size={18} /> },
    { to: '/admin/llm-settings', label: 'Queue settings', icon: <Settings size={18} /> },
    { to: '/admin/llm-costs', label: 'LLM Cost Accounting', icon: <DollarSign size={18} /> },
        { to: '/admin/categories/hybrid/cache', label: 'Hybrid Categorization Cache', icon: <Sparkles size={18} /> },
        { to: '/admin/categories/exclusion-phrases', label: 'QR Exclusion Phrases', icon: <Sparkles size={18} /> },
    { to: '/admin/llm-cache', label: 'LLM Image Queue Cache', icon: <Layers size={18} /> },
    { to: '/admin/receipt-image-assets', label: 'Receipt Image Assets', icon: <Layers size={18} /> },
    { to: '/admin/queue-workers', label: 'Workers Stats', icon: <Package size={18} /> },
    { to: '/admin/legal', label: 'Legal Management', icon: <Layers size={18} /> },
    { to: '/admin/selectors', label: 'Selectors', icon: <Search size={18} /> },
    { to: '/admin/metrics', label: 'Metrics', icon: <Bell size={18} /> },
    { to: '/admin/exchange-rates', label: 'Exchange Rates', icon: <DollarSign size={18} /> },
    { to: '/admin/languages', label: 'Languages', icon: <Globe2 size={18} /> },
    { to: '/admin/translations', label: 'Translations', icon: <MessageSquare size={18} /> },
    { to: '/admin/public-recognitions', label: 'Public API Recognitions', icon: <Layers size={18} /> },
    { to: '/admin/currency-transition-rules', label: 'Currency Rules', icon: <DollarSign size={18} /> },
    { to: '/admin/currency-normalization', label: 'Currency Normalization', icon: <DollarSign size={18} /> },
    { to: '/admin/mobile/releases', label: 'APK Releases', icon: <Package size={18} /> },
  ]

  const publicApiAccessMenu = [
    { to: '/admin/mobile-devices', label: 'Mobile Devices', icon: <Smartphone size={18} /> },
    { to: '/admin/telegram-mini-app-users', label: 'Telegram Mini App Users', icon: <MessageSquare size={18} /> },
    { to: '/admin/public-api/limits', label: 'Limit Policies', icon: <Layers size={18} /> },
    { to: '/admin/public-api/usage', label: 'Usage', icon: <Bell size={18} /> },
  ]

  const [openGroups, setOpenGroups] = useState(new Set(menuGroups.map((group) => group.title)))

  const toggleGroup = (groupTitle) => {
    setOpenGroups((prev) => {
      const next = new Set(prev)
      if (next.has(groupTitle)) {
        next.delete(groupTitle)
      } else {
        next.add(groupTitle)
      }
      return next
    })
  }

  const getBreadcrumbs = (pathname) => {
    if (pathname.startsWith('/receipts/')) {
      return ['Dashboard', 'Receipt detail']
    }

    const segments = pathname.split('/').filter(Boolean)
    if (segments.length === 0) return ['Dashboard']

    const crumbs = []
    let current = ''
    segments.forEach((segment) => {
      current += `/${segment}`
      crumbs.push(routeLabels[current] || segment.replace(/-/g, ' ').replace(/\b\w/g, (match) => match.toUpperCase()))
    })

    return segments.length === 1 ? crumbs : ['Dashboard', ...crumbs]
  }

  const breadcrumbs = getBreadcrumbs(location.pathname)
  const pageTitle = breadcrumbs[breadcrumbs.length - 1] || 'Dashboard'

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <aside className={`flex h-full flex-col border-r border-outline bg-surface transition-all duration-300 ${isOpen ? 'w-64' : 'w-16'}`}>
        <div className="flex items-center justify-between p-4">
          <Link to="/" className="flex items-center gap-2 text-lg font-bold tracking-tight text-primary">
            <img src={brandMark} alt="Savinfy mark" className="h-7 w-7 rounded" />
            {isOpen ? 'Savinfy Receipts' : 'SR'}
          </Link>
          <button
            onClick={() => {
              const next = !isOpen
              setIsOpen(next)
              window.localStorage.setItem(SIDEBAR_STORAGE_KEY, next.toString())
            }}
            className="rounded bg-surface-elevated p-1 hover:opacity-90"
          >
            {isOpen ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
          </button>
        </div>
        <nav className="mt-2 flex flex-col gap-1 px-1">
          {menuGroups.map((group) => (
            <div key={group.title} className="space-y-1">
              {isOpen && (
                <button
                  type="button"
                  onClick={() => toggleGroup(group.title)}
                  className="flex w-full items-center justify-between rounded-lg px-2 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground hover:bg-surface-elevated"
                >
                  <span>{group.title}</span>
                  <ChevronRight
                    size={16}
                    className={`transition-transform ${openGroups.has(group.title) ? 'rotate-90' : ''}`}
                  />
                </button>
              )}
              <div className={`space-y-1 ${isOpen && !openGroups.has(group.title) ? 'hidden' : ''}`}>
                {group.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end
                    className={({ isActive }) =>
                      `flex items-center gap-3 rounded-lg px-2 py-2 text-sm font-medium transition ${
                        isActive ? 'bg-primary text-primary-foreground shadow-sm' : 'text-foreground hover:bg-surface-elevated'
                      }`
                    }
                  >
                    {item.icon}
                    {isOpen && item.label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}

          {Boolean(user?.is_admin) && (
            <div className="mt-4 space-y-1">
              {isOpen && <div className="px-3 py-2 text-xs uppercase tracking-[0.12em] text-muted-foreground">Administration</div>}
              {adminMenu.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-lg px-2 py-2 text-sm font-medium transition ${
                      isActive ? 'bg-primary text-primary-foreground shadow-sm' : 'text-foreground hover:bg-surface-elevated'
                    }`
                  }
                >
                  {item.icon}
                  {isOpen && item.label}
                </NavLink>
              ))}
              {isOpen && <div className="px-3 pt-4 pb-2 text-xs uppercase tracking-[0.12em] text-muted-foreground">Public API Access</div>}
              {publicApiAccessMenu.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-lg px-2 py-2 text-sm font-medium transition ${
                      isActive ? 'bg-primary text-primary-foreground shadow-sm' : 'text-foreground hover:bg-surface-elevated'
                    }`
                  }
                >
                  {item.icon}
                  {isOpen && item.label}
                </NavLink>
              ))}
            </div>
          )}
        </nav>

        <div className="mt-auto p-4">
          <button onClick={handleLogout} className="flex w-full items-center gap-2 rounded-lg bg-danger px-3 py-2 text-sm font-semibold text-white hover:opacity-90">
            <LogOut size={16} />{isOpen && 'Logout'}
          </button>
        </div>
      </aside>

      <div className="flex flex-1 flex-col overflow-visible">
        <header className="flex items-center justify-between border-b border-outline bg-surface px-4 py-3 shadow-sm">
          <div className="flex flex-col gap-1">
            <h1 className="text-lg font-semibold">{pageTitle}</h1>
            <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              {breadcrumbs.map((crumb, index) => (
                <span key={crumb} className="inline-flex items-center gap-2">
                  {index > 0 && <span className="text-muted-foreground">/</span>}
                  <span>{crumb}</span>
                </span>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <div className="relative">
              <Search size={16} className="absolute left-2 top-2 text-muted-foreground" />
              <input type="text" placeholder="Search..." className="w-64 rounded-lg border border-outline bg-surface-elevated px-8 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30" />
            </div>
            <NotificationBell />
            <Link to="/profile" className="rounded-lg border border-outline bg-surface p-2 text-muted-foreground hover:bg-surface-elevated">
              <User size={18} />
            </Link>
          </div>
        </header>

        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

